import {Box, Button, ColumnLayout, ExpandableSection, FormField, Header, Input, Tabs, TabsProps} from '@amzn/awsui-components-react'
import React, {useContext, useEffect, useState} from 'react'
import {isValidFnsku, isValidNumericId} from '../../validation/InputValidator'
import {MetadataKey, MetadataQuery} from '../../backend/model'
import GlobalDataContext from '../../context/GlobalDataProvider'
import {useHistory} from 'react-router-dom'
import {convertToPropertyFilterQuery, convertToQuery, getPropertyFiltersFromMetadataKeys} from '../search/SearchUtils'
import {PropertyFilterQuery} from '@amzn/awsui-collection-hooks'
import * as queryString from 'query-string'
import {
    ADVANCED_SEARCH_TAB_ID,
    SIMPLE_SEARCH_TAB_ID,
} from '../metadata/Constants'
import {fetchIapMetadataKeys} from '../../backend/backend'
import AdvancedSearch from '../search/AdvancedSearch'
import {METADATA_I18N_STRINGS} from '../metadata/MetadataSearchProperty'

interface IapSearchPanelProps {
    iapId?: string
    fnsku?: string
    initialTab?: string
    advancedSearchQuery?: MetadataQuery
    simpleSearchHandler: (iapId?: string, fnsku?: string) => void
    advancedSearchHandler: (query: MetadataQuery) => void
}

const IapSearchPanel: React.FC<IapSearchPanelProps> = (props) => {
    const [iapId, setIapId] = useState(props.iapId)
    const [fnsku, setFnsku] = useState(props.fnsku)
    const {iapMetadataKeys, setIapMetadataKeys} = useContext(GlobalDataContext)
    const history = useHistory()
    const displayedQuery = convertToPropertyFilterQuery(props.advancedSearchQuery!)


    useEffect(() => {
        if (iapMetadataKeys.length === 0) {
            fetchMetadataKeys().then(metadataKeysResponse => {
                setIapMetadataKeys(metadataKeysResponse)
            })
        }
    }, [iapMetadataKeys])

    useEffect(() => {
        setIapId(props.iapId)
        setFnsku(props.fnsku)
    }, [props])

    function executeSearchOnEnter(key: string): void {
        if (key === 'Enter') {
            props.simpleSearchHandler(iapId, fnsku)
        }
    }

    async function fetchMetadataKeys(): Promise<MetadataKey[]> {
        return (await fetchIapMetadataKeys()).data.map((metadataKey) => metadataKey.iapMetadataKeyObject)
    }

    const metadataSearchHandler = (query: PropertyFilterQuery) => {
        let iapQuery = convertToQuery(query)
        props.advancedSearchHandler(iapQuery)
    }

    const simpleSearch = <ColumnLayout columns={3}>
        <FormField label="IAP ID">
            <Input
                id="iapSearchPanel-iapId"
                invalid={!!iapId && !isValidNumericId(iapId)}
                value={iapId || ''}
                onChange={({detail}) => setIapId(detail.value.trim())}
                onKeyDown={({detail}) => executeSearchOnEnter(detail.key)}
            />
        </FormField>
        <FormField label="FNSKU ID (optional)">
            <Input
                id="iapSearchPanel-fnsku"
                invalid={!!fnsku && fnsku.length > 0 && !isValidFnsku(fnsku)}
                value={fnsku || ''}
                onChange={({detail}) => setFnsku(detail.value.trim())}
                onKeyDown={({detail}) => executeSearchOnEnter(detail.key)}
            />
        </FormField>
        <Box margin={{top: 'xl'}}>
            <Button variant="primary" onClick={() => props.simpleSearchHandler(iapId, fnsku)}>Search</Button>
        </Box>
    </ColumnLayout>

    const [filterProperties, filterOptions] = getPropertyFiltersFromMetadataKeys(iapMetadataKeys)

    const advancedSearch = (
        <AdvancedSearch label={"Metadata Keys"}
            searchHandler={metadataSearchHandler}
            displayedQuery={displayedQuery}
            filteringProperties={filterProperties}
            filteringOptions={filterOptions}
            displaySearchButton={true}
            i18nStrings={METADATA_I18N_STRINGS}
        />
    )

    const handleTabSwitch = (detail: TabsProps.ChangeDetail) => {
        const basePath = '/iaps'

        const newUrl = queryString.stringifyUrl({
            url: basePath,
            query: {
                view: detail.activeTabId,
            },
        })
        history.push(newUrl)
    }

    const searchTab = (
        <Tabs
            onChange={({detail}) => handleTabSwitch(detail)}
            activeTabId={props.initialTab}
            tabs={[
                {
                    label: 'Simple Search',
                    id: SIMPLE_SEARCH_TAB_ID,
                    content: simpleSearch,
                },
                {
                    label: 'Advanced Search',
                    id: ADVANCED_SEARCH_TAB_ID,
                    content: advancedSearch,
                },
            ]}
        />
    )

    return (
        <ExpandableSection
            id="iapSearchPanel"
            variant="container"
            header={<Header variant="h2">Search</Header>}
            defaultExpanded>
            {searchTab}
        </ExpandableSection>
    )
}

export default IapSearchPanel
